import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMG } from "../../data/site";
import { PATHS } from "../../data/content";
import { MaskLines, EASE } from "../Motion";
import { Img } from "../Img";
import { CtaExt } from "../Bits";

const Router = () => (
  <motion.ol initial={{ y: 24 }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.5 }} className="mt-14 grid border-t border-line md:grid-cols-3" aria-label="Choose your situation" data-testid="hero-path-router">
    {PATHS.map((p) => (
      <li key={p.n} className="border-b border-line md:border-b-0 md:border-r md:last:border-r-0">
        <Link to={p.to} className="group flex h-full min-h-[44px] flex-col gap-3 py-6 md:px-6 md:first:pl-0" data-testid={`hero-path-${p.n}`}>
          <span className="font-mono text-xs text-flame-soft">{p.n}</span>
          <span className="font-display text-2xl uppercase leading-none tracking-tight text-ink transition-colors group-hover:text-flame">{p.situation}</span>
          <span className="mt-auto inline-flex items-center gap-2 text-sm text-mist">{p.action}<ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      </li>
    ))}
  </motion.ol>
);

export const HomeHero = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 18 });
  const sry = useSpring(ry, { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const py = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const rect = useRef(null);
  const onEnter = () => { rect.current = ref.current.getBoundingClientRect(); };
  const onMove = (e) => {
    const r = rect.current;
    if (!r) return;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    ref.current.style.setProperty("--mx", `${x * 100}%`);
    ref.current.style.setProperty("--my", `${y * 100}%`);
    ry.set((x - 0.5) * 10);
    rx.set((0.5 - y) * 8);
  };

  return (
    <section ref={ref} onMouseEnter={onEnter} onMouseMove={onMove} aria-labelledby="hero-h" className="hero-spot grain relative overflow-hidden bg-bone2 pb-16 pt-14 text-ink sm:pt-20" data-testid="home-hero">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <motion.p initial={{ y: 10 }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.05 }} className="eyebrow text-flame-soft">
              Denver metro · furnace repair · replacement · tune-ups
            </motion.p>
            <h1 id="hero-h" className="mt-6 font-display text-[3.2rem] uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-[6.2rem]">
              <MaskLines delay={0.02} lines={["Heat at 5,280 ft,", "diagnosed", <span key="h" className="text-flame">honestly.</span>]} />
            </h1>
            <motion.p initial={{ y: 16 }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.3 }} className="mt-8 max-w-xl text-lg leading-relaxed text-mist">
              A diagnosis-first furnace company for Denver homeowners who want the problem explained, the options laid out, and the decision left to them.
            </motion.p>
            <motion.div initial={{ y: 16 }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.4 }} className="mt-10 flex flex-wrap gap-4">
              <Link to="/services/furnace-repair" className="btn-flame group" data-testid="hero-primary-cta">
                My furnace stopped <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <CtaExt testid="hero-external-cta" />
            </motion.div>
          </div>
          <div className="[perspective:1200px]">
            <motion.div style={{ rotateX: srx, rotateY: sry, y: py }} className="clip-frame relative">
              <Img img={IMG.homeHero} priority className="aspect-[4/3] w-full" sizes="(min-width: 1024px) 40vw, 100vw" testid="hero-image" />
              <motion.div aria-hidden="true" initial={{ scaleY: 1 }} animate={{ scaleY: 0 }} transition={{ duration: 1.3, ease: EASE, delay: 0.15 }} className="absolute inset-0 z-10 origin-top bg-bone2" />
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between bg-gradient-to-t from-ink/90 to-transparent p-5 pr-16">
                <span className="font-mono text-xs text-bone/90">Denver bungalow · winter evening</span>
                <span className="font-mono text-xs text-bone">5,280 ft</span>
              </div>
            </motion.div>
          </div>
        </div>
        <Router />
      </div>
    </section>
  );
};
